/**
 * Pure, view-agnostic logic for the translation-overrides editor (O2D-PRD-092).
 *
 * These functions are extracted from `<ha-translation-editor-view>` so they can be
 * unit-tested without a DOM: segment derivation (the tab list), orphan detection
 * (draft keys no longer backed by `de.json`), and full-set save-payload building
 * (all non-empty draft entries across every segment → a plain `Record<string,string>`).
 *
 * All methods are `public static` pure functions (helper pattern) — no instance state.
 */
export class TranslationEditorHelper {

    /** Segment separator — the first key segment names the tab. Must match `TranslationFlattenHelper`. */
    private static readonly SEPARATOR = '.';

    /**
     * The segment (tab) a key belongs to: its first dot-separated part. A key with
     * no separator is its own segment. Single source of truth for segment membership
     * so the tab derivation and the per-segment row filtering stay consistent.
     */
    public static segmentOf(key: string): string {
        const separatorIndex = key.indexOf(this.SEPARATOR);
        return separatorIndex === -1 ? key : key.slice(0, separatorIndex);
    }

    /**
     * Derive the ordered, distinct list of segments (tabs) from the base key set.
     * The segment is the first dot-separated part of each key; a key with no dot is
     * its own segment. Order follows first appearance so the tab order is stable.
     */
    public static deriveSegments(base: Map<string, string>): string[] {
        const segments: string[] = [];
        const seen = new Set<string>();
        for (const key of base.keys()) {
            const segment = this.segmentOf(key);
            if (!seen.has(segment)) {
                seen.add(segment);
                segments.push(segment);
            }
        }
        return segments;
    }

    /**
     * Orphan keys: draft override keys that are no longer present in the base
     * (`de.json`) set. Shown in the "veraltet" section, never silently dropped.
     * Order follows the draft's insertion order.
     */
    public static findOrphanKeys(draft: Map<string, string>, base: Map<string, string>): string[] {
        const orphans: string[] = [];
        for (const key of draft.keys()) {
            if (!base.has(key)) {
                orphans.push(key);
            }
        }
        return orphans;
    }

    /**
     * Count the filter matches per segment across ALL segments, so every tab can
     * show a match-count badge (a hit that lives only in another segment stays
     * discoverable). A row (one base key) matches when the lower-cased needle is a
     * substring of its key, its original text, OR its draft override — the SAME
     * case-insensitive OR-match `<o2d-table>`'s filter uses, so the badge count and
     * the visible rows agree. An empty filter matches every row (badge = segment
     * total). Every derived segment is present in the map, with 0 when nothing hits.
     */
    public static countMatchesPerSegment(
        base: Map<string, string>,
        draft: Map<string, string>,
        filter: string,
    ): Map<string, number> {
        const needle = filter.toLowerCase();
        const counts = new Map<string, number>();
        for (const [key, original] of base) {
            const segment = this.segmentOf(key);
            const override = draft.get(key) ?? '';
            const isMatch = this.rowMatches(key, original, override, needle);
            counts.set(segment, (counts.get(segment) ?? 0) + (isMatch ? 1 : 0));
        }
        return counts;
    }

    /**
     * Whether a row's key/original/override contains the (already lower-cased)
     * needle — the case-insensitive OR-match mirroring `TableFilterController`. An
     * empty needle is a substring of everything, so it matches every row.
     */
    private static rowMatches(key: string, original: string, override: string, needle: string): boolean {
        return key.toLowerCase().includes(needle)
            || original.toLowerCase().includes(needle)
            || override.toLowerCase().includes(needle);
    }

    /**
     * Whether the draft differs from the last-saved snapshot in what a SAVE would
     * persist. Both are reduced via `buildPayload` (empty overrides dropped) and
     * compared order-independently, so the dirty state means exactly "a save would
     * change the persisted set" — clearing an override to empty equals removing it,
     * and Map insertion order does not matter. Drives the save-button/`beforeunload`
     * dirty guard; re-baselining the snapshot after a save returns it to clean.
     */
    public static isDirty(draft: Map<string, string>, snapshot: Map<string, string>): boolean {
        const draftPayload = this.buildPayload(draft);
        const snapshotPayload = this.buildPayload(snapshot);

        const draftKeys = Object.keys(draftPayload);
        if (draftKeys.length !== Object.keys(snapshotPayload).length) {
            return true;
        }
        return draftKeys.some(key => draftPayload[key] !== snapshotPayload[key]);
    }

    /**
     * Build the full-set save payload: every non-empty draft entry across ALL
     * segments (including orphans) as a plain `Record<string,string>`. Empty values
     * are dropped (an empty override means "no override" — mirrors the backend).
     */
    public static buildPayload(draft: Map<string, string>): Record<string, string> {
        const payload: Record<string, string> = {};
        for (const [key, value] of draft) {
            if (value !== '') {
                payload[key] = value;
            }
        }
        return payload;
    }
}
